/* SpawnAnything — destructible terrain, client side (see src/sim/features/crater.js for the simulation).
   The worker sends every crater as [x, z, R, D]; they are replayed on the local terrain copy (or on a flat
   grid for the flat field). Only the touched chunks are rebuilt, as overlay meshes with earth and scorch
   colours; the original ground above carved cells is cut away in the shader (mask texture, also in the
   shadow pass). Grass in a crater vanishes, trees and props tip over. Setup option: "Gelände zerstörbar". */
import { CRATER_CHUNK, craterClear, craterFlatGrid, craterStamp } from '../sim/features/crater.js';
import { heightAt, inBounds } from '../world/terrain.js';
import { DEFAULT_PALETTE } from '../world/terrainMesh.js';
import { injectVoxelJitter } from '../world/groundDetail.js';
import './crater.css';

const EARTH = 0x7d5838, CHAR = 0x2b2420, WALL = 0x634329, BED = 0x4f4b3b, OUTLINE = 0x1e1a15;
const OFF = 0.015;   // overlay walls in front of the original cliff they cover (no z-fighting)
const PER_FRAME = 6; // chunk rebuilds per frame
const BK = 8;        // prop index bucket (m)

// discard the original ground over carved cells: tops by their own cell, walls by the cell they belong to
// (walls face away from their cell, so step back half a cell against the normal)
function inject(sh, U) {
  Object.assign(sh.uniforms, U);
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'varying vec3 vCrW; varying vec3 vCrN;\nvoid main() {')
    .replace('#include <project_vertex>', '#include <project_vertex>\nvCrW = (modelMatrix * vec4(transformed, 1.0)).xyz; vCrN = mat3(modelMatrix) * normal;');
  sh.fragmentShader = sh.fragmentShader.replace('void main() {', `uniform sampler2D uCrMask; uniform vec4 uCrGrid; varying vec3 vCrW; varying vec3 vCrN;
void main() {
  if (uCrGrid.w > 0.5) {
    vec3 crN = normalize(vCrN);
    vec2 crP = vCrW.xz - (abs(crN.y) < 0.5 ? crN.xz * uCrGrid.z * 0.25 : vec2(0.0));
    vec2 crUV = (crP - uCrGrid.xy) / (uCrGrid.z * uCrGrid.w);
    if (crUV.x > 0.0 && crUV.y > 0.0 && crUV.x < 1.0 && crUV.y < 1.0 && texture2D(uCrMask, crUV).r > 0.5) discard;
  }`);
}

/**
 * terrain(): current terrain or null, env(): environment meshes (grass, props), groundH(x, z): ground height,
 * resetup(): re-send the battle setup after the option changed.
 */
export function createCrater({ THREE, scene, terrainMat, groundMat, toonGrad, terrain, env, groundH, resetup }) {
  let on = true;
  try { if (localStorage.getItem('aw-crater') === '0') on = false; } catch (e) { }
  let cur = null, flat = null, vis = null, mask = null, raf = 0;
  const chunks = new Map();
  const lin = h => { const c = new THREE.Color(h); return [c.r, c.g, c.b]; };
  const C = { earth: lin(EARTH), char: lin(CHAR), wall: lin(WALL), bed: lin(BED), outline: lin(OUTLINE), rock: DEFAULT_PALETTE.rock.map(lin) };

  // --- shader hooks: original terrain mesh (+ its shadow) and the flat ground plane ---
  const blank = new THREE.DataTexture(new Uint8Array([0]), 1, 1, THREE.RedFormat); blank.needsUpdate = true;
  const UT = { uCrMask: { value: blank }, uCrGrid: { value: new THREE.Vector4(0, 0, 1, 0) } };
  const UG = { uCrMask: { value: blank }, uCrGrid: { value: new THREE.Vector4(0, 0, 1, 0) } };
  const hook = (mat, U, key) => {
    const prev = mat.onBeforeCompile, prevKey = mat.customProgramCacheKey;
    mat.onBeforeCompile = function (sh, r) { prev.call(this, sh, r); inject(sh, U); };
    mat.customProgramCacheKey = function () { return prevKey.call(this) + key; };
    mat.needsUpdate = true;
  };
  hook(terrainMat, UT, '|crater'); hook(groundMat, UG, '|crater');
  const depthMat = new THREE.MeshDepthMaterial();
  depthMat.onBeforeCompile = sh => inject(sh, UT);
  depthMat.customProgramCacheKey = () => 'crater-depth';
  const mat = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: toonGrad });
  mat.onBeforeCompile = injectVoxelJitter; mat.customProgramCacheKey = () => 'crater-gd'; // 0.5 m sub-voxel jitter like the terrain

  function setMask(t) {
    if (mask && mask.t === t) return;
    if (mask) mask.tex.dispose();
    const data = new Uint8Array(t.n * t.n), tex = new THREE.DataTexture(data, t.n, t.n, THREE.RedFormat, THREE.UnsignedByteType);
    tex.magFilter = tex.minFilter = THREE.NearestFilter; tex.generateMipmaps = false; tex.unpackAlignment = 1; tex.needsUpdate = true;
    mask = { t, data, tex };
    const U = t.craterFlat ? UG : UT;
    U.uCrMask.value = tex; U.uCrGrid.value.set(t.origin, t.origin, t.cell, t.n);
  }

  // --- chunk meshes ---
  const buf = () => ({ p: [], n: [], c: [], w: [], i: [] });
  const V = new Float64Array(12);
  function quad(B, nx, ny, nz, c0, c1, c2, c3) {
    const v = B.p.length / 3;
    for (let k = 0; k < 4; k++) { B.p.push(V[k * 3], V[k * 3 + 1], V[k * 3 + 2]); B.n.push(nx, ny, nz); }
    if (c0) B.c.push(...c0, ...c1, ...c2, ...c3); else B.w.push(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    const ax = V[3] - V[0], ay = V[4] - V[1], az = V[5] - V[2], bx = V[6] - V[0], by = V[7] - V[1], bz = V[8] - V[2];
    const flip = (ay * bz - az * by) * nx + (az * bx - ax * bz) * ny + (ax * by - ay * bx) * nz < 0;
    if (!flip) B.i.push(v, v + 1, v + 2, v, v + 2, v + 3); else B.i.push(v, v + 2, v + 1, v, v + 3, v + 2);
  }
  const setV = (k, x, y, z) => { V[k * 3] = x; V[k * 3 + 1] = y; V[k * 3 + 2] = z; };
  const mul = (c, m) => [c[0] * m, c[1] * m, c[2] * m];
  const mix = (a, b, f) => [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
  const hash = i => (Math.imul(i ^ (i >>> 13), 0x5bd1e995) >>> 0) / 4294967296;
  const CHc = new Float64Array(4);
  // corner heights of a cell [x0z0, x1z0, x0z1, x1z1] as the original mesh draws them (carved: flat top)
  function corners(t, j) {
    const sh = t.stepHeight;
    if (t.cut[j]) { CHc.fill(t.cutH[j]); return CHc; }
    if (t.water[j] > 0) { CHc.fill(t.waterLevel[j] * sh - t.bedDepth); return CHc; }
    const L = t.level[j], rd = t.rampDir[j];
    if (rd < 0) { CHc.fill(L * sh); return CHc; }
    const lo = (L + t.rampK[j] / t.rampLen) * sh, hi = (L + (t.rampK[j] + 1) / t.rampLen) * sh;
    const hx1 = rd === 0, hx0 = rd === 2, hz1 = rd === 1, hz0 = rd === 3;
    CHc[0] = (hx0 || hz0) ? hi : lo; CHc[1] = (hx1 || hz0) ? hi : lo; CHc[2] = (hx0 || hz1) ? hi : lo; CHc[3] = (hx1 || hz1) ? hi : lo;
    return CHc;
  }
  const DXS = [1, 0, -1, 0], DZS = [0, 1, 0, -1];
  function buildChunk(t, k) {
    const n = t.n, nc = Math.ceil(n / CRATER_CHUNK), cell = t.cell, o = t.origin, sh = t.stepHeight, skirt = -sh * 1.5 - 0.5;
    const x0 = (k % nc) * CRATER_CHUNK, z0 = ((k / nc) | 0) * CRATER_CHUNK, x1 = Math.min(n, x0 + CRATER_CHUNK), z1 = Math.min(n, z0 + CRATER_CHUNK);
    const T = buf(), Wb = buf();
    const band = y => C.rock[(((Math.floor(y / sh + 1e-4)) % 4) + 4) % 4];
    for (let gz = z0; gz < z1; gz++) for (let gx = x0; gx < x1; gx++) {
      const i = gz * n + gx;
      mask.data[i] = t.cut[i] ? 255 : 0;
      if (!t.cut[i]) continue;
      const a = t.cutH[i], X0 = o + gx * cell, Z0 = o + gz * cell, X1 = X0 + cell, Z1 = Z0 + cell, wl = t.cutW[i];
      // top: earth, darker and more scorched the deeper the crater
      const q = Math.min(1, Math.max(0, (t.cutO[i] - a) / 1.6));
      const tc = wl ? C.bed : mul(mix(C.earth, C.char, 0.2 + q * 0.7), 0.9 + hash(i) * 0.18);
      setV(0, X0, a, Z0); setV(1, X0, a, Z1); setV(2, X1, a, Z1); setV(3, X1, a, Z0); quad(T, 0, 1, 0, tc, tc, tc, tc);
      if (wl && vis) { const y = (wl - 1) * sh - t.waterDrop; setV(0, X0, y, Z0); setV(1, X0, y, Z1); setV(2, X1, y, Z1); setV(3, X1, y, Z0); quad(Wb, 0, 1, 0); }
      // walls on the four sides
      for (let d = 0; d < 4; d++) {
        const x2 = gx + DXS[d], z2 = gz + DZS[d], j = (x2 < 0 || z2 < 0 || x2 >= n || z2 >= n) ? -1 : z2 * n + x2;
        let b0 = skirt, b1 = skirt;
        if (j >= 0) {
          const c = corners(t, j);
          if (d === 0) { b0 = c[0]; b1 = c[2]; } else if (d === 2) { b0 = c[1]; b1 = c[3]; } else if (d === 1) { b0 = c[0]; b1 = c[1]; } else { b0 = c[2]; b1 = c[3]; }
        }
        const ox = DXS[d], oz = DZS[d], xw = d === 0 ? X1 : X0, zw = d === 1 ? Z1 : Z0;
        const s0 = (d & 1) ? X0 : Z0, s1 = (d & 1) ? X1 : Z1;
        const put = (m, s, y, e) => (d & 1) ? setV(m, s, y, e) : setV(m, e, y, s);
        const ew = (d & 1) ? zw : xw;
        if (a > b0 + 1e-4 || a > b1 + 1e-4) {
          // this cell is higher: its own wall down to the neighbour, facing it
          const l0 = Math.min(a, b0), l1 = Math.min(a, b1);
          put(0, s0, l0, ew); put(1, s0, a, ew); put(2, s1, a, ew); put(3, s1, l1, ew);
          const cb = mul(C.wall, 0.62); quad(T, ox, 0, oz, cb, C.wall, C.wall, cb);
          if (a - Math.min(l0, l1) > 0.3) strip(T, d, ew, s0, s1, a + 0.03, a + 0.03, -1);
        }
        if ((b0 > a + 1e-4 || b1 > a + 1e-4) && !(j >= 0 && t.cut[j])) {
          // the untouched neighbour is higher: its wall now reaches down into the crater
          const h0 = Math.max(a, b0), h1 = Math.max(a, b1), e = ew - (ox + oz) * OFF;
          put(0, s0, a, e); put(1, s0, h0, e); put(2, s1, h1, e); put(3, s1, a, e);
          const cb = mul(C.wall, 0.62), ct = Math.max(h0, h1) > t.cutO[i] + 0.1 ? band(Math.max(h0, h1) - 0.01) : C.wall;
          quad(T, -ox, 0, -oz, cb, ct, ct, cb);
          if (Math.max(h0, h1) - a > 0.3) strip(T, d, ew, s0, s1, b0 + 0.03, b1 + 0.03, 1);
        }
      }
    }
    // dark comic strip on a top along the edge line e; side -1 = into this cell, 1 = into the neighbour
    function strip(B, d, e, s0, s1, y0, y1, side) {
      const w = 0.2 * side * ((d === 0 || d === 1) ? 1 : -1);
      if (d & 1) { setV(0, s0, y0, e); setV(1, s1, y1, e); setV(2, s1, y1, e + w); setV(3, s0, y0, e + w); }
      else { setV(0, e, y0, s0); setV(1, e, y1, s1); setV(2, e + w, y1, s1); setV(3, e + w, y0, s0); }
      quad(B, 0, 1, 0, C.outline, C.outline, C.outline, C.outline);
    }
    dropChunk(k);
    if (!T.i.length) return;
    const geo = (B, water) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(B.p, 3)); g.setAttribute('normal', new THREE.Float32BufferAttribute(B.n, 3));
      if (water) g.setAttribute('aWater', new THREE.Float32BufferAttribute(B.w, 3)); else g.setAttribute('color', new THREE.Float32BufferAttribute(B.c, 3));
      g.setIndex(B.i); g.computeBoundingSphere();
      return g;
    };
    const mesh = new THREE.Mesh(geo(T, false), mat);
    mesh.castShadow = true; mesh.receiveShadow = true; mesh.name = 'crater';
    scene.add(mesh);
    let water = null;
    if (Wb.i.length && vis) { water = new THREE.Mesh(geo(Wb, true), vis.waterMesh.material); water.receiveShadow = true; water.renderOrder = 1; scene.add(water); }
    chunks.set(k, { mesh, water });
  }
  function dropChunk(k) {
    const c = chunks.get(k); if (!c) return;
    c.mesh.removeFromParent(); c.mesh.geometry.dispose();
    if (c.water) { c.water.removeFromParent(); c.water.geometry.dispose(); }
    chunks.delete(k);
  }
  function pump() {
    raf = 0;
    const t = cur; if (!t || !t.cutDirty || !mask) return;
    const D = t.cutDirty; let done = 0;
    for (let k = 0; k < D.length && done < PER_FRAME; k++) {
      if (!D[k]) continue;
      D[k] = 0; t.cutDirtyN--; done++;
      buildChunk(t, k);
    }
    if (done) mask.tex.needsUpdate = true; // the mask follows the rebuilt chunks: no holes in between
    if (t.cutDirtyN > 0) raf = requestAnimationFrame(pump);
  }

  // --- props: grass vanishes, trees and props tip over (away from the blast), everything follows the ground ---
  let envRef = null, envIdx = [];
  const M = new THREE.Matrix4(), P = new THREE.Vector3(), Qt = new THREE.Quaternion(), S = new THREE.Vector3(), Qk = new THREE.Quaternion(), AX = new THREE.Vector3();
  const bkey = (x, z) => (Math.floor(x / BK) + 32768) * 65536 + Math.floor(z / BK) + 32768;
  function propIndex() {
    const list = env();
    if (list === envRef) return envIdx;
    envRef = list; envIdx = [];
    const seen = new Set();
    for (const im of list) {
      if (!im.isInstancedMesh || seen.has(im.instanceMatrix)) continue; // outlines share the matrices of their prop
      seen.add(im.instanceMatrix);
      if (!im.geometry.boundingBox) im.geometry.computeBoundingBox();
      const a = im.instanceMatrix.array, B = new Map();
      for (let k = 0; k < im.count; k++) { const key = bkey(a[k * 16 + 12], a[k * 16 + 14]); let l = B.get(key); if (!l) B.set(key, l = []); l.push(k); }
      envIdx.push({ im, tall: im.geometry.boundingBox.max.y > 1.2, B, saved: new Map(), fallen: new Set() });
    }
    return envIdx;
  }
  function props(x, z, R) {
    const Ro = R * 1.6;
    for (const e of propIndex()) {
      const a = e.im.instanceMatrix.array; let ch = false;
      for (let bx = Math.floor((x - Ro) / BK); bx <= Math.floor((x + Ro) / BK); bx++) for (let bz = Math.floor((z - Ro) / BK); bz <= Math.floor((z + Ro) / BK); bz++) {
        const l = e.B.get((bx + 32768) * 65536 + bz + 32768); if (!l) continue;
        for (const k of l) {
          const o = k * 16, px = a[o + 12], pz = a[o + 14], dx = px - x, dz = pz - z, d = Math.sqrt(dx * dx + dz * dz);
          if (d >= Ro) continue;
          const first = !e.saved.has(k);
          if (!e.tall && d < R) {
            if (!first && a[o] === 0 && a[o + 5] === 0) continue;
            if (first) e.saved.set(k, a.slice(o, o + 16));
            for (let m = 0; m < 11; m++) a[o + m] = 0; // scale 0: gone
            ch = true; continue;
          }
          const gy = groundH(px, pz);
          if (e.tall && d < R * 1.15 && !e.fallen.has(k)) {
            if (first) e.saved.set(k, a.slice(o, o + 16));
            e.fallen.add(k);
            M.fromArray(a, o); M.decompose(P, Qt, S);
            const l2 = d > 0.01 ? d : 1, ux = d > 0.01 ? dx / l2 : 1, uz = d > 0.01 ? dz / l2 : 0;
            Qk.setFromAxisAngle(AX.set(uz, 0, -ux), 1.2 + Math.random() * 0.35); // top falls away from the blast
            Qt.premultiply(Qk); P.y = gy; M.compose(P, Qt, S); M.toArray(a, o);
            ch = true; continue;
          }
          if (gy < a[o + 13] - 0.05) { if (first) e.saved.set(k, a.slice(o, o + 16)); a[o + 13] = gy; ch = true; }
        }
      }
      if (ch) e.im.instanceMatrix.needsUpdate = true;
    }
  }
  function restoreProps() {
    if (env() !== envRef) { envRef = null; envIdx = []; return; } // rebuilt meanwhile: nothing to restore
    for (const e of envIdx) {
      if (!e.saved.size) continue;
      const a = e.im.instanceMatrix.array;
      for (const [k, m] of e.saved) a.set(m, k * 16);
      e.saved.clear(); e.fallen.clear(); e.im.instanceMatrix.needsUpdate = true;
    }
  }

  // --- setup option ---
  function mountUI() {
    const anchor = document.getElementById('terrainNew'), row = anchor && anchor.closest('.row');
    if (!row) return;
    const el = document.createElement('div');
    el.className = 'crater-row';
    el.title = 'Explosionen reißen Krater ins Gelände, sprengen Hügel weg und laufen am Fluss voll Wasser.';
    el.innerHTML = '<span class="crater-lbl" id="craterLbl">Gelände zerstörbar</span><div class="seg crater-seg" role="group" aria-labelledby="craterLbl"><button type="button" data-crater="1">An</button><button type="button" data-crater="0">Aus</button></div>';
    row.after(el);
    const btns = el.querySelectorAll('[data-crater]');
    const sync = () => btns.forEach(b => b.setAttribute('aria-pressed', String((b.dataset.crater === '1') === on)));
    btns.forEach(b => b.addEventListener('click', () => {
      const v = b.dataset.crater === '1'; if (v === on) return;
      on = v; sync();
      try { localStorage.setItem('aw-crater', on ? '1' : '0'); } catch (e) { }
      resetup();
    }));
    sync();
  }
  mountUI();

  function reset() {
    if (cur) craterClear(cur, false);
    cur = null; flat = null;
    for (const k of [...chunks.keys()]) dropChunk(k);
    if (mask) { mask.tex.dispose(); mask = null; }
    UT.uCrMask.value = UG.uCrMask.value = blank; UT.uCrGrid.value.w = 0; UG.uCrGrid.value.w = 0;
    restoreProps();
  }
  return {
    /** setup-message fields */
    msg: () => ({ crater: on }),
    /** new battle / new map: undo every crater (the worker does the same on its setup) */
    reset,
    /** the original terrain mesh was (re)built: cut it away in the shadow pass too, flooded craters use its water */
    bind(v) {
      vis = v; if (!v) return;
      v.terrainMesh.customDepthMaterial = depthMat;
      for (const c of chunks.values()) if (c.water) c.water.material = v.waterMesh.material;
    },
    /** worker message { type: 'crater', rec: Float64Array [x, z, R, D]…, reset, flat: { n, cell } | null } */
    onWorker(m) {
      if (m.reset) reset(); // replay seek / clip: all craters of the battle follow in this message
      let t;
      if (m.flat) {
        if (!flat || flat.n !== m.flat.n || flat.cell !== m.flat.cell) { reset(); flat = craterFlatGrid(m.flat.n, m.flat.cell); }
        t = flat;
      } else t = terrain();
      if (!t) return;
      if (cur !== t) { if (cur) reset(); if (m.flat) flat = t; cur = t; }
      setMask(t);
      const r = m.rec;
      for (let k = 0; k + 3 < r.length; k += 4) if (craterStamp(t, r[k], r[k + 1], r[k + 2], r[k + 3], false)) props(r[k], r[k + 1], r[k + 2]);
      if (t.cutDirtyN && !raf) raf = requestAnimationFrame(pump);
    },
    /** ground height on the flat field (0 without craters) */
    flatH: (x, z) => flat && inBounds(flat, x, z) ? heightAt(flat, x, z) : 0,
  };
}
